import {
  AvailabilityResponseSchema,
  AppointmentListResponseSchema,
  AppointmentResponseSchema,
  AvailabilityRulesResponseSchema,
  PublicAvailabilityResponseSchema,
  type AvailabilityQuery,
  type AppointmentCreateBody,
  type AppointmentUpdateStatus,
  type AppointmentListQuery,
  type AvailabilityRuleUpsert,
} from '../../src/shared/scheduling';
import { apiGet, apiPost } from './client';

export const schedulingKeys = {
  appointments: {
    all: (slug: string) => ['appointments', slug] as const,
    list: (slug: string, filter?: string) =>
      ['appointments', slug, 'list', filter ?? 'all'] as const,
    detail: (slug: string, id: string) => ['appointments', slug, id] as const,
  },
  availability: {
    slots: (slug: string, startsAt: number, endsAt: number) =>
      ['availability', slug, startsAt, endsAt] as const,
  },
  availabilityRules: {
    all: (slug: string) => ['availabilityRules', slug] as const,
  },
};

export function fetchAvailability(
  slug: string,
  query: AvailabilityQuery,
  signal?: AbortSignal
): Promise<import('../../src/shared/scheduling').AvailabilityResponse> {
  const params = new URLSearchParams();
  params.set('startsAt', String(query.startsAt));
  params.set('endsAt', String(query.endsAt));
  if (query.userId) params.set('userId', query.userId);
  if (query.locationId) params.set('locationId', query.locationId);
  return apiGet(
    `/api/w/${slug}/availability?${params.toString()}`,
    AvailabilityResponseSchema,
    signal
  );
}

export function fetchAppointments(
  slug: string,
  query?: AppointmentListQuery,
  signal?: AbortSignal
): Promise<import('../../src/shared/scheduling').AppointmentListResponse> {
  const params = new URLSearchParams();
  if (query?.startsAt !== undefined) params.set('startsAt', String(query.startsAt));
  if (query?.endsAt !== undefined) params.set('endsAt', String(query.endsAt));
  if (query?.status !== undefined) params.set('status', query.status);
  if (query?.assignedUserId !== undefined) params.set('assignedUserId', query.assignedUserId);
  if (query?.cursor !== undefined) params.set('cursor', query.cursor);
  if (query?.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return apiGet(
    `/api/w/${slug}/appointments${qs ? `?${qs}` : ''}`,
    AppointmentListResponseSchema,
    signal
  );
}

export function fetchAppointment(
  slug: string,
  id: string,
  signal?: AbortSignal
): Promise<import('../../src/shared/scheduling').Appointment> {
  return apiGet(`/api/w/${slug}/appointments/${id}`, AppointmentResponseSchema, signal);
}

export function createAppointment(
  slug: string,
  body: AppointmentCreateBody
): Promise<import('../../src/shared/scheduling').Appointment> {
  return apiPost(`/api/w/${slug}/appointments`, body, AppointmentResponseSchema);
}

export function updateAppointmentStatus(
  slug: string,
  id: string,
  body: AppointmentUpdateStatus
): Promise<import('../../src/shared/scheduling').Appointment> {
  return apiPost(`/api/w/${slug}/appointments/${id}/status`, body, AppointmentResponseSchema);
}

export function cancelAppointment(
  slug: string,
  id: string
): Promise<import('../../src/shared/scheduling').Appointment> {
  return apiPost(`/api/w/${slug}/appointments/${id}/cancel`, {}, AppointmentResponseSchema);
}

export function fetchAvailabilityRules(
  slug: string,
  signal?: AbortSignal
): Promise<import('../../src/shared/scheduling').AvailabilityRulesResponse> {
  return apiGet(
    `/api/w/${slug}/settings/availability-rules`,
    AvailabilityRulesResponseSchema,
    signal
  );
}

export function updateAvailabilityRules(
  slug: string,
  body: AvailabilityRuleUpsert[]
): Promise<import('../../src/shared/scheduling').AvailabilityRulesResponse> {
  return apiPost(
    `/api/w/${slug}/settings/availability-rules`,
    { rules: body },
    AvailabilityRulesResponseSchema
  );
}

export function fetchPublicAvailability(
  slug: string,
  startsAt: number,
  endsAt: number,
  signal?: AbortSignal
): Promise<import('../../src/shared/scheduling').PublicAvailabilityResponse> {
  void startsAt;
  void endsAt;
  return apiGet(`/api/book/${slug}`, PublicAvailabilityResponseSchema, signal);
}

export function createPublicBooking(
  slug: string,
  body: {
    contactName: string;
    phoneE164: string;
    email?: string;
    slotStartsAt: number;
    locationId?: string;
    assignedUserId?: string;
    turnstileToken: string;
  }
): Promise<import('../../src/shared/scheduling').Appointment> {
  return apiPost(`/api/book/${slug}`, body, AppointmentResponseSchema);
}
